import { useCallback } from 'react'
import { useInterviewStore, InterviewStage, StageDataItem } from '@/store/interviewStore'
import { buildApiUrl, getApiHeaders } from '@/config/api'

// Define interfaces for different stage data types
// Type guards
function isCodingData(data: any): data is { code: string; isDirty: boolean } {
  return data && typeof data.code === 'string' && typeof data.isDirty === 'boolean';
}

function isDatabaseData(data: any): data is { query: string; isDirty: boolean } {
  return data && typeof data.query === 'string' && typeof data.isDirty === 'boolean';
}

function isSystemDesignData(data: any): data is { content: string; placedComponents: any[]; isDirty: boolean } {
  return data && typeof data.content === 'string' && Array.isArray(data.placedComponents) && typeof data.isDirty === 'boolean';
}

function isWhiteboardData(data: any): data is { content: string; canvasData: any; isDirty: boolean } {
  return data && typeof data.content === 'string' && data.canvasData && typeof data.isDirty === 'boolean';
}

function isDataModelingData(data: any): data is {
  content: string;
  entities: any[];
  foreignKeys: any[];
  comments: any[];
  zoom: number;
  panX: number;
  panY: number;
  isDirty: boolean;
} {
  return (
    data &&
    typeof data.content === 'string' &&
    Array.isArray(data.entities) &&
    Array.isArray(data.foreignKeys) &&
    Array.isArray(data.comments) &&
    typeof data.zoom === 'number' &&
    typeof data.panX === 'number' &&
    typeof data.panY === 'number' &&
    typeof data.isDirty === 'boolean'
  );
}

function isUMLData(data: any): data is {
  content: string;
  classes: any[];
  relationships: any[];
  isDirty: boolean;
} {
  return (
    data &&
    typeof data.content === 'string' &&
    Array.isArray(data.classes) &&
    Array.isArray(data.relationships) &&
    typeof data.isDirty === 'boolean'
  );
}

export function useNavigationWithSave() {
  const store = useInterviewStore()

  const saveCurrentIfDirty = useCallback(async () => {
    const { activeStage, activeProblemId, sessionId, interviewId } = store

    if (!sessionId || !interviewId) return

    let isDirty = false
    let dataToSave = null

    if (activeStage === 'coding' && activeProblemId) {
      const codingData = store.getCodingData(activeProblemId)
      if (isCodingData(codingData)) {
        isDirty = codingData.isDirty
        dataToSave = codingData
      }
    } else if (activeStage === 'database-questions' && activeProblemId) {
      const databaseData = store.getDatabaseData(activeProblemId)
      if (isDatabaseData(databaseData)) {
        isDirty = databaseData.isDirty
        dataToSave = databaseData
      }
    } else if (activeStage === 'system-design' || activeStage === 'data-modeling' || activeStage === 'uml' || activeStage === 'whiteboard') {
      const stageData = store.getStageData(activeStage)
      if (stageData) {
        isDirty = stageData.isDirty
        dataToSave = stageData
      }
    }

    if (isDirty && dataToSave) {
      console.log(`🔄 Immediate save before navigation from ${activeStage}${activeProblemId ? ` problem ${activeProblemId}` : ''}`)
      
      try {
        // Public endpoint doesn't require authentication token
        const token = localStorage.getItem('token')
        if (!token) {
          console.log('ℹ️ No auth token found, using public endpoint for navigation save')
        }

        let endpoint: string
        let requestBody: any

        const interviewUuid = store.interviewId || undefined

        if (activeStage === 'coding' && activeProblemId && isCodingData(dataToSave)) {
          endpoint = 'interview-sessions/public/save-coding-input'
          requestBody = {
            sessionId,
            interviewId: interviewUuid,
            content: dataToSave.code || '',
            saveType: 'manual',
            language: 'javascript',
            problemId: parseInt(activeProblemId),
            problemTitle: `Problem ${activeProblemId}`
          }
        } else if (activeStage === 'database-questions' && activeProblemId && isDatabaseData(dataToSave)) {
          endpoint = 'interview-sessions/public/save-input'
          requestBody = {
            sessionId,
            interviewId: interviewUuid,
            inputType: 'database_query',
            inputCategory: 'database_questions',
            content: JSON.stringify({ query: dataToSave.query, problemId: activeProblemId }),
            saveType: 'manual'
          }
        } else if (activeStage === 'system-design' && isSystemDesignData(dataToSave)) {
          endpoint = 'interview-responses/public/save/system_design'
          requestBody = {
            interviewId: interviewUuid,
            content: dataToSave.content || '',
            save_type: 'manual',
            diagram_elements: {
              placedComponents: dataToSave.placedComponents || [],
              connections: dataToSave.connections || [],
              selectedTool: dataToSave.selectedTool || 'move',
              designMode: dataToSave.designMode || 'visual'
            },
            design_notes: dataToSave.content || ''
          }
        } else if (activeStage === 'whiteboard' && isWhiteboardData(dataToSave)) {
          endpoint = 'interview-responses/public/save/whiteboard'
          requestBody = {
            interviewId: interviewUuid,
            content: dataToSave.content || '',
            save_type: 'manual',
            canvas_elements: dataToSave.canvasData ? { canvasData: dataToSave.canvasData } : null,
            canvas_size: {
              selectedTool: dataToSave.selectedTool || 'pen',
              selectedColor: dataToSave.selectedColor || '#000000',
              strokeWidth: dataToSave.strokeWidth || 2
            }
          }
        } else if (activeStage === 'data-modeling' && isDataModelingData(dataToSave)) {
          endpoint = 'data-modeling-responses/public/save'
          requestBody = {
            sessionId,
            interviewId: interviewUuid,
            content: dataToSave.content || '',
            saveType: 'manual',
            entitiesData: JSON.stringify(dataToSave.entities || []),
            foreignKeysData: JSON.stringify(dataToSave.foreignKeys || []),
            commentsData: JSON.stringify(dataToSave.comments || []),
            zoomPanState: JSON.stringify({
              zoom: dataToSave.zoom || 1,
              panX: dataToSave.panX || 0,
              panY: dataToSave.panY || 0
            })
          }
        } else if (activeStage === 'uml' && isUMLData(dataToSave)) {
          endpoint = 'interview-responses/public/save/uml_design'
          requestBody = {
            interviewId: interviewUuid,
            content: dataToSave.content || '',
            save_type: 'manual',
            classes_data: dataToSave.classes || [],
            relationships_data: dataToSave.relationships || [],
            diagram_type: 'class'
          }
        } else {
          // For any other unsupported stages, skip saving to avoid errors
          console.log(`⚠️ Stage ${activeStage} not supported for navigation save, skipping`)
          return
        }

        const response = await fetch(buildApiUrl(endpoint), {
          method: 'POST',
          headers: {
            ...getApiHeaders(false), // Public endpoint, no auth required
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(requestBody)
        })

        if (response.ok) {
          // Mark as clean
          if (activeStage === 'coding' && activeProblemId) {
            store.markCodingClean(activeProblemId)
          } else if (activeStage === 'database-questions' && activeProblemId) {
            store.markDatabaseClean(activeProblemId)
          } else if (activeStage === 'system-design' || activeStage === 'data-modeling' || activeStage === 'uml' || activeStage === 'whiteboard') {
            const stageData = store.getStageData(activeStage)
            if (stageData) {
              store.markStageClean(activeStage)
            }
          }
          console.log(`✅ Immediate save successful for ${activeStage}${activeProblemId ? ` problem ${activeProblemId}` : ''}`)
        }
      } catch (error) {
        console.error('❌ Immediate save failed:', error)
      }
    }
  }, [store])

  const switchStage = useCallback(async (newStage: InterviewStage) => {
    // Save current stage if dirty
    await saveCurrentIfDirty()

    // Update active stage
    store.setActiveStage(newStage)

    // Clear problem ID when leaving coding or database-questions
    if (newStage !== 'coding' && newStage !== 'database-questions') {
      store.setActiveProblemId(null)
    }
  }, [store, saveCurrentIfDirty])

  const switchProblem = useCallback(async (problemId: string) => {
    // Save current problem if in coding stage and dirty
    if (store.activeStage === 'coding' && store.activeProblemId) {
      await saveCurrentIfDirty()
    }

    // Set active stage to coding if not already
    if (store.activeStage !== 'coding') {
      store.setActiveStage('coding')
    }
    
    store.setActiveProblemId(problemId)
  }, [store, saveCurrentIfDirty])

  const switchDatabaseProblem = useCallback(async (problemId: string) => {
    // Save current problem if in database-questions stage and dirty
    if (store.activeStage === 'database-questions' && store.activeProblemId) {
      await saveCurrentIfDirty()
    }

    // Set active stage to database-questions if not already
    if (store.activeStage !== 'database-questions') {
      store.setActiveStage('database-questions')
    }
    
    store.setActiveProblemId(problemId)
  }, [store, saveCurrentIfDirty])

  return {
    activeStage: store.activeStage,
    activeProblemId: store.activeProblemId,
    switchStage,
    switchProblem,
    switchDatabaseProblem
  }
}
